import { Building2, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicAboutContent } from "@/hooks/api/use-public";
import { Skeleton } from "@/components/ui/skeleton";
import { DynamicIcon } from "@/components/ui/DynamicIcon";

const About = () => {
  const { t, language } = useLanguage();
  const { data: aboutContent, isLoading } = usePublicAboutContent();

  // API returns { success: true, data: { content: {...}, values: [...] } }
  const content = aboutContent?.data?.content;
  const values = aboutContent?.data?.values?.filter((v: any) => v.is_active) || [];

  // Get content from API - no static fallback, use shimmer loading instead
  const heroTitle = content?.hero_title
    ? (typeof content.hero_title === 'string'
        ? content.hero_title
        : content.hero_title[language] || content.hero_title.en)
    : null;

  const heroSubtitle = content?.hero_subtitle
    ? (typeof content.hero_subtitle === 'string'
        ? content.hero_subtitle
        : content.hero_subtitle[language] || content.hero_subtitle.en)
    : null;

  const visionTitle = content?.vision_title
    ? (typeof content.vision_title === 'string'
        ? content.vision_title
        : content.vision_title[language] || content.vision_title.en)
    : null;

  const visionContent = content?.vision_content
    ? (typeof content.vision_content === 'string'
        ? content.vision_content
        : content.vision_content[language] || content.vision_content.en)
    : null;

  const missionTitle = content?.mission_title
    ? (typeof content.mission_title === 'string'
        ? content.mission_title
        : content.mission_title[language] || content.mission_title.en)
    : null;

  const missionContent = content?.mission_content
    ? (typeof content.mission_content === 'string'
        ? content.mission_content
        : content.mission_content[language] || content.mission_content.en)
    : null;

  const valuesSectionTitle = content?.values_section_title
    ? (typeof content.values_section_title === 'string'
        ? content.values_section_title
        : content.values_section_title[language] || content.values_section_title.en)
    : null;

  return (
    <div className="py-16">
      {/* Hero */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {isLoading || !heroTitle ? (
              <>
                <Skeleton className="h-12 w-full max-w-2xl mx-auto mb-6" />
                <Skeleton className="h-6 w-full max-w-xl mx-auto" />
              </>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-up">
                  {heroTitle}
                </h1>
                <p className="text-lg text-muted-foreground animate-fade-up stagger-1">
                  {heroSubtitle}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {isLoading || !visionTitle ? (
            <div className="grid md:grid-cols-2 gap-12">
              <Skeleton className="h-64 rounded-2xl" />
              <Skeleton className="h-64 rounded-2xl" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-card p-10 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                  <Target size={32} />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-4">{visionTitle}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {visionContent}
                </p>
              </div>
              <div className="bg-card p-10 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                  <Building2 size={32} />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-4">{missionTitle}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {missionContent}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">
            {valuesSectionTitle || <Skeleton className="h-9 w-40 inline-block" />}
          </h2>
          {isLoading || values.length === 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-64 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value) => {
                const title = typeof value.title === 'string'
                  ? value.title
                  : (value.title?.[language] || value.title?.en || '');
                const content = typeof value.content === 'string'
                  ? value.content
                  : (value.content?.[language] || value.content?.en || '');

                return (
                  <div key={value.id} className="text-center">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-6">
                      {value.icon ? (
                        <DynamicIcon 
                          iconName={value.icon} 
                          className="w-10 h-10 text-primary" 
                          size={40}
                          fallback={<span className="text-2xl">⭐</span>}
                        />
                      ) : (
                        <span className="text-2xl">⭐</span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                    <p className="text-muted-foreground">
                      {content}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default About;
